/* eslint import/namespace: ['error', { allowComputed: true }] */
import captureSpecificError from '../../sentry';
import * as icons from '../../../components/shared/vector-images';
import {
  imgElementSvg,
  navigationArrows,
} from '../../../components/shared/vector-images';
import { telemetry } from '../../backend';
import { boundsOverlapsViewableArea } from '../../geofencing';
import { getStore } from '../../store';
import { layerExists, setDataSource, sourceExists } from '../_helpers';
import { hasFeatureEnabled } from '../../feature';

let isShown;

const loadIconImages = async ({ map, images }) => {
  const loadImagePromises = images.forEach(async ([imageId, url]) => {
    // Add the icon images from the endpoint to the map if not already loaded
    if (imageId && !map.hasImage(imageId)) {
      await map.loadImage(url, async (error, image) => {
        if (error) {
          captureSpecificError(
            error,
            'containers/app-main-client/src/services/map-layer/custom-layers/navigation.js',
          );
        } else {
          await map.addImage(imageId, image);
        }
      });
    }
  });
  await Promise.all(loadImagePromises);
};

const checkValidity = async ({ map, source, imageIds }) => {
  imageIds.forEach((imageId) => {
    if (!imageId || !map.hasImage(imageId)) {
      telemetry(
        // eslint-disable-next-line no-bitwise
        telemetry.WARN | telemetry.NOTIFY_SYSTEM_CHANNEL,
        `[navigation mapLayer] Invalid ${source} aton of ${imageId} requested.`,
      );
    }
  });
};

const initializeBase = (map) => {
  map.addLayer({
    id: 'navigation',
    type: 'raster',
    minzoom: 11,
    source: {
      type: 'raster',
      tiles: ['https://t1.openseamap.org/seamark/{z}/{x}/{y}.png'],
      tileSize: 256,
    },
  });
};

const initializeNsw = (map) => {
  if (!sourceExists(map, 'navigationNsw')) {
    map.addSource('navigationNsw', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    (async () => {
      try {
        const response = await fetch(
          '/public/app-main/interface-gov-au-nsw/atons.json',
        );
        const symbols = await response.json();
        const promiseArray = Object.keys(symbols).map(async (symbol) => {
          const name = `aton${symbol}`;
          const icon = icons[name];
          if (!icon) {
            telemetry(
              // eslint-disable-next-line no-bitwise
              telemetry.WARN | telemetry.NOTIFY_SYSTEM_CHANNEL,
              `[navigation mapLayer] Invalid aton of ${icon} requested. This needs to be added to the codebase as an SVG`,
            );
          } else {
            const marker = await imgElementSvg(icons[name](), {
              width: 75,
              height: 75,
            });
            if (!map.hasImage(name)) map.addImage(name, marker);
          }
          return true;
        });
        await Promise.all(promiseArray);
        const data = {
          type: 'FeatureCollection',
          features: Object.keys(symbols).reduce(
            (returnArray, symbol) => [
              ...returnArray,
              ...symbols[symbol].map((coordinates) => ({
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates,
                },
                properties: {
                  symbol,
                },
              })),
            ],
            [],
          ),
        };
        setDataSource(map, 'navigationNsw', data);
      } catch (error) {
        telemetry(
          telemetry.WARN,
          '[navigation mapLayer] Failed to load nsw aton data',
        );
      }
    })();
  }
  if (!layerExists(map, 'navigationNsw')) {
    map.addLayer({
      id: 'navigationNsw',
      type: 'symbol',
      source: 'navigationNsw',
      layout: {
        'icon-image': ['concat', 'aton', ['to-string', ['get', 'symbol']]],
        'icon-size': 0.5,
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
      },
      paint: {
        'icon-opacity': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
      },
    });
  }
};

const initializeWa = (map) => {
  (async () => {
    try {
      const response = await fetch(
        '/public/app-main/interface-gov-au-wa/icons.json',
      );
      const uniqueIconReferences = await response.json();
      uniqueIconReferences.forEach((iconReference) => {
        if (!iconReference || !map.hasImage(iconReference)) {
          telemetry(
            // eslint-disable-next-line no-bitwise
            telemetry.WARN | telemetry.NOTIFY_SYSTEM_CHANNEL,
            `[navigation mapLayer] Invalid WA aton of ${iconReference} requested. This needs to be added to the sprite sheet.`,
          );
        }
      });
    } catch (error) {
      telemetry(
        telemetry.WARN,
        '[navigation mapLayer] Failed to load WA aton data',
      );
    }
  })();
  if (!layerExists(map, 'navigationWa')) {
    map.addLayer({
      id: 'navigationWa',
      type: 'symbol',
      source: 'deckee-static',
      'source-layer': 'navigationWa',
      layout: {
        'icon-image': [
          'concat',
          'ATON_WA__',
          ['to-string', ['get', 'icontoshow']],
        ],
        'icon-size': 1,
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
      },
      paint: {
        'icon-opacity': ['interpolate', ['linear'], ['zoom'], 11.99, 0, 12, 1],
      },
    });
  }
};

const initializeNSWv2 = (map) => {
  (async () => {
    try {
      const iconResponse = await fetch(
        '/public/app-main/icon-composer/icons.json',
      );
      const iconResponseJson = await iconResponse.json();
      if (iconResponseJson && Object.entries(iconResponseJson)?.length > 0) {
        await loadIconImages({ map, images: Object.entries(iconResponseJson) });
        checkValidity({
          map,
          source: 'nswv2',
          imageIds: Object.keys(iconResponseJson),
        });
      }
    } catch (error) {
      telemetry(
        telemetry.WARN,
        `[navigation mapLayer] Failed to load NSW V2 aton data ${error}`,
      );
    }
  })();
  if (!layerExists(map, 'navigationNSWv2')) {
    map.addLayer({
      id: 'navigationNSWv2',
      type: 'symbol',
      source: 'deckee-static',
      'source-layer': 'navigationNSWv2',
      layout: {
        'icon-image': ['to-string', ['get', 'icontoshow']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          11,
          0,
          12,
          0.2,
          14,
          0.2,
          16,
          0.3,
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
      },
      paint: {
        'icon-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 1],
      },
    });
  }
};

const initializeQld = (map) => {
  (async () => {
    try {
      const iconResponse = await fetch(
        '/public/app-main/icon-composer/icons.json',
      );
      const iconResponseJson = await iconResponse.json();
      if (iconResponseJson && Object.entries(iconResponseJson)?.length > 0) {
        await loadIconImages({ map, images: Object.entries(iconResponseJson) });
        checkValidity({
          map,
          source: 'queensland',
          imageIds: Object.keys(iconResponseJson),
        });
      }
    } catch (error) {
      telemetry(
        telemetry.WARN,
        `[navigation mapLayer] Failed to load Qld aton data ${error}`,
      );
    }
  })();
  if (!layerExists(map, 'navigationQld')) {
    map.addLayer({
      id: 'navigationQld',
      type: 'symbol',
      source: 'deckee-static',
      'source-layer': 'navigationQld',
      layout: {
        'icon-image': ['to-string', ['get', 'icontoshow']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          11,
          0,
          12,
          0.2,
          14,
          0.2,
          16,
          0.3,
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
      },
      paint: {
        'icon-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 1],
      },
    });
  }
};

const initializeTas = (map) => {
  (async () => {
    try {
      const iconResponse = await fetch(
        '/public/app-main/icon-composer/icons.json',
      );
      const iconResponseJson = await iconResponse.json();
      if (iconResponseJson && Object.entries(iconResponseJson)?.length > 0) {
        await loadIconImages({ map, images: Object.entries(iconResponseJson) });
        checkValidity({
          source: 'tasmania',
          imageIds: Object.keys(iconResponseJson),
        });
      }
    } catch (error) {
      telemetry(
        telemetry.WARN,
        `[navigation mapLayer] Failed to load Tas aton data ${error}`,
      );
    }
  })();
  if (!layerExists(map, 'navigationTas')) {
    map.addLayer({
      id: 'navigationTas',
      type: 'symbol',
      source: 'deckee-static',
      'source-layer': 'navigationTas',
      layout: {
        'icon-image': ['to-string', ['get', 'icontoshow']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          11,
          0,
          12,
          0.2,
          14,
          0.2,
          16,
          0.3,
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
      },
      paint: {
        'icon-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 1],
      },
    });
  }
};

const initializeUSA = (map) => {
  (async () => {
    try {
      const iconResponse = await fetch(
        '/public/app-main/icon-composer/icons.json',
      );
      const iconResponseJson = await iconResponse.json();
      if (iconResponseJson && Object.entries(iconResponseJson)?.length > 0) {
        await loadIconImages({ map, images: Object.entries(iconResponseJson) });
        checkValidity({
          source: 'USCG',
          imageIds: Object.keys(iconResponseJson),
        });
      }
    } catch (error) {
      telemetry(
        telemetry.WARN,
        `[navigation mapLayer] Failed to load USCG aton data ${error}`,
      );
    }
  })();
  if (!layerExists(map, 'navigationUSA')) {
    map.addLayer({
      id: 'navigationUSA',
      type: 'symbol',
      source: 'deckee-static',
      'source-layer': 'navigationUSA',
      layout: {
        'icon-image': ['to-string', ['get', 'icontoshow']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          11,
          0,
          12,
          0.2,
          14,
          0.2,
          16,
          0.3,
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
      },
      paint: {
        'icon-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 1],
      },
    });
  }
};

const updateLayers = (map) => {
  const insideVic = boundsOverlapsViewableArea('VICTORIA', map);
  const insideSa = boundsOverlapsViewableArea('SOUTHAUSTRALIA', map);
  const insideNsw = boundsOverlapsViewableArea('NEWSOUTHWALES', map);
  const insideTas = boundsOverlapsViewableArea('TASMANIA', map);
  const insideWa = boundsOverlapsViewableArea('WESTERNAUSTRALIA', map);
  const insideQld = boundsOverlapsViewableArea('QUEENSLAND', map);
  const insideUSA = boundsOverlapsViewableArea('UNITEDSTATES', map);
  const insideAlaska = boundsOverlapsViewableArea('ALASKA', map);
  const insideHawaii = boundsOverlapsViewableArea('HAWAII', map);

  const showHideNavigationLayer = (layerName, shown, initializeFunction) => {
    try {
      const layerInitialized = layerExists(map, layerName);
      if (!shown && !layerInitialized) {
        // Do nothing
      } else if (shown && !layerInitialized) {
        telemetry(
          telemetry.DEBUG,
          `[navigation mapLayer] initializing navigation layer ${layerName}`,
        );
        initializeFunction(map);
      } else if (shown && layerInitialized) {
        if (map.getLayoutProperty(layerName, 'visibility') !== 'visible') {
          telemetry(
            telemetry.DEBUG,
            `[navigation mapLayer] showing layer ${layerName}`,
          );
          map.setLayoutProperty(layerName, 'visibility', 'visible');
        }
      } else if (!shown && layerInitialized) {
        if (map.getLayoutProperty(layerName, 'visibility') !== 'none') {
          telemetry(
            telemetry.DEBUG,
            `[navigation mapLayer] hiding layer ${layerName}`,
          );
          map.setLayoutProperty(layerName, 'visibility', 'none');
        }
      }
    } catch (error) {
      telemetry(
        telemetry.ERROR,
        `[navigation mapLayer] Failed to toggle ${layerName}`,
      );
    }
  };

  const { hidingAllLayers } = getStore().getState().appControls;
  showHideNavigationLayer(
    'gps-validation-markers symbol',
    isShown && !hidingAllLayers,
  );
  showHideNavigationLayer(
    'navigationUSA',
    isShown && (insideUSA || insideHawaii || insideAlaska) && !hidingAllLayers,
    initializeUSA,
  );
  showHideNavigationLayer(
    'navigationQld',
    isShown && insideQld && !hidingAllLayers,
    initializeQld,
  );
  showHideNavigationLayer(
    'navigationTas',
    isShown && insideTas && !hidingAllLayers,
    initializeTas,
  );
  showHideNavigationLayer(
    'navigationWa',
    isShown && insideWa && !hidingAllLayers,
    initializeWa,
  );
  if (hasFeatureEnabled('nsw-v2-atons')) {
    showHideNavigationLayer(
      'navigationNSWv2',
      isShown && insideNsw && !hidingAllLayers,
      initializeNSWv2,
    );
  } else {
    showHideNavigationLayer(
      'navigationNsw',
      isShown && insideNsw && !hidingAllLayers,
      initializeNsw,
    );
  }
  showHideNavigationLayer(
    'navigation',
    isShown && (insideVic || insideSa) && !hidingAllLayers,
    initializeBase,
  );
};

const layer = {
  displayName: 'Aids to navigation',
  synonyms: '',
  group: 'customLayer',
  typeUrl: 'aton',
  icon: {
    baseShape: navigationArrows,
    iconColor: '#fff',
    markerColor: '#E28D18',
  },
  shownByDefault: true,
  relativeDepth: 20,
  menuOptions: {
    visible: true,
    owningMenu: 'safety',
    order: 0,
  },
  onInitialize: () => {
    // We initialize layers in the map update event for this layer as it depends on where we
    // currently are on the map when creating layers.
  },
  onToggle: (map, show) => {
    isShown = show;
    updateLayers(map);
  },
  onMapUpdateEvent: updateLayers,
};

export default layer;
